import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './client'
import type {
  ScheduledTask,
  ListResult,
  CreateScheduledTaskRequest,
  UpdateScheduledTaskRequest,
} from '@/types'

export function useScheduledTasks(tenantId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['scheduled-tasks', tenantId],
    queryFn: () =>
      api.get<ListResult<ScheduledTask>>(`/api/v1/tenants/${tenantId}/scheduled-tasks`),
    enabled: !!tenantId,
    refetchInterval: 15000,
  })
}

export function useScheduledTask(tenantId: string | null, taskId: string | null) {
  const api = useApiClient()
  return useQuery({
    queryKey: ['scheduled-task', tenantId, taskId],
    queryFn: () =>
      api.get<ScheduledTask>(`/api/v1/tenants/${tenantId}/scheduled-tasks/${taskId}`),
    enabled: !!tenantId && !!taskId,
  })
}

export function useCreateScheduledTask(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: CreateScheduledTaskRequest) =>
      api.post<ScheduledTask>(`/api/v1/tenants/${tenantId}/scheduled-tasks`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', tenantId] }),
  })
}

export function useUpdateScheduledTask(tenantId: string, taskId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (req: UpdateScheduledTaskRequest) =>
      api.put<ScheduledTask>(`/api/v1/tenants/${tenantId}/scheduled-tasks/${taskId}`, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', tenantId] }),
  })
}

export function useDeleteScheduledTask(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) =>
      api.del(`/api/v1/tenants/${tenantId}/scheduled-tasks/${taskId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', tenantId] }),
  })
}

export function useToggleScheduledTask(tenantId: string) {
  const api = useApiClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, enabled }: { taskId: string; enabled: boolean }) =>
      api.put<ScheduledTask>(`/api/v1/tenants/${tenantId}/scheduled-tasks/${taskId}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', tenantId] }),
  })
}
